import React from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { ArrowLeft, X } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  TYPOGRAPHY,
  COMPANION_PANEL_WIDTH,
  PLAN_PANEL_HEADER_HEIGHT,
  SIDEBAR_RAIL_WIDTH,
} from '../../theme';
import { useUIStore, selectRightDockInset } from '../../store/uiStore';
import { resolvePanelLayout } from '../../utils/panelLayout';
import { useEscapeToClose } from '../../utils/escapeStack';
import { DockedPanel } from './DockedPanel';

/**
 * Whether a companion attached to a left dock of `parentWidth` should render
 * beside its parent or fold into it as a drill-in (panel-system.md's panel
 * budget). Recomputed from the live window and store state rather than read
 * off `dockInsets` directly — the registry already reflects whatever fold
 * decision is in effect, so it can't also be the input to the next one.
 *
 * Deviates from the zero-argument signature sketched in the phase-0 spec:
 * resolvePanelLayout needs the parent's width to build its `left` input,
 * and nothing else in the store carries "my own parent's width" for an
 * arbitrary caller, so this takes it as a parameter (CompanionPanel already
 * requires it as a prop for the same reason).
 */
export function useCompanionPlacement(parentWidth: number): 'beside' | 'folded' {
  const { width: windowWidth } = useWindowDimensions();
  const rightWidth = useUIStore(selectRightDockInset);
  const newest = useUIStore((s) => s.panelNewest);

  const result = resolvePanelLayout({
    availableWidth: windowWidth - SIDEBAR_RAIL_WIDTH,
    left: { width: parentWidth, companionWidth: COMPANION_PANEL_WIDTH },
    right: rightWidth > 0 ? { width: rightWidth } : undefined,
    newest,
  });
  // Always 'beside' or 'folded' in practice — companionWidth is always
  // given above, so resolvePanelLayout never returns null for it.
  return result.companion ?? 'folded';
}

interface CompanionPanelProps {
  /** The parent dock's own registration id — folded into this companion's
   *  own id so two companions on different docks never collide. */
  attachTo: string;
  /** The parent dock's current width, in px. */
  parentWidth: number;
  title: string;
  /** Small caps line above the title naming the task ("Assign"), so the
   *  title can be the object it's about ("Thin trees"). Matches the
   *  panel-system prototype's companion header. */
  overline?: string;
  /** One line under the title (the question the task answers). */
  subtitle?: string;
  onClose: () => void;
  /** True when useCompanionPlacement() said 'folded': this component then
   *  covers its parent instead of sitting beside it — same DockedPanel,
   *  same children, just at offset 0 with width = parentWidth and a
   *  back arrow instead of a close X (panel-system.md's "drill-in", updated
   *  Sept 2026 — this used to render nothing here and rely on the caller
   *  pushing an equivalent view into the parent's own PanelStack; now
   *  CompanionPanel does the covering itself, so the element tree — and
   *  crucially `children`'s own mounted state, e.g. typed form fields —
   *  stays identical across a resize that crosses the fold threshold).
   *  CompanionPanel never decides this itself — it only obeys it. */
  folded: boolean;
  children: React.ReactNode;
  testID?: string;
  /** Exit-animation controls, same contract as DockedPanel's (see
   *  useLeavingPanel). Omit both for an instant close. */
  shown?: boolean;
  onExited?: () => void;
}

// A companion emerges from beside its parent, not from the screen edge: it
// starts this far to the left of its final spot (over the parent's right
// edge) and fades in as it slides out.
const COMPANION_SLIDE_PX = 56;

/**
 * A task about one item in a docked panel — Assign, edit party, plan
 * updates (panel-system.md's "companion"). Wraps DockedPanel side='left',
 * in `surfaceAlt` so it reads as secondary to its parent in both themes.
 *
 * `folded` (see its doc comment) only changes offset/width and the header's
 * button — beside its parent (offset = parentWidth, width =
 * COMPANION_PANEL_WIDTH) or covering it (offset 0, width = parentWidth).
 * Always the same DockedPanel, always the same `children`: this component
 * never returns null and never conditionally omits `children` from the
 * tree, so a fold triggered by a browser resize can't remount whatever's
 * inside (a typed party name, mid-edit).
 *
 * Deliberately does not import the tab-bar hold hook (useSheetTabBarHold or
 * similar) — companions must not acquire that refcount, same as a plain
 * DockedPanel today.
 */
export function CompanionPanel({
  attachTo,
  parentWidth,
  title,
  overline,
  subtitle,
  onClose,
  folded,
  children,
  testID,
  shown = true,
  onExited,
}: CompanionPanelProps) {
  const { colors } = useTheme();
  // Escape closes this layer in both modes — folded still covers the map
  // with a real, visible surface, not a no-op placeholder.
  useEscapeToClose(shown, onClose);

  // Suffixed with attachTo rather than a bare 'companion-panel': DockedPanel
  // keys its dock-inset registration off this same id, and two companions
  // on two different parent docks must not collide there. (Deviation from
  // the phase-0 spec's literal "testID default companion-panel" — reported
  // alongside this file.)
  const id = testID ?? `companion-panel-${attachTo}`;
  const headingId = `${id}-heading`;

  return (
    <DockedPanel
      testID={id}
      side="left"
      width={folded ? parentWidth : COMPANION_PANEL_WIDTH}
      offset={folded ? 0 : parentWidth}
      backgroundColor="surfaceAlt"
      slideDistance={COMPANION_SLIDE_PX}
      fade
      animateLayout
      shown={shown}
      onExited={onExited}
      scroll={false}
      ariaLabelledBy={headingId}>
      <View style={styles.fill}>
        {/* Pinned: only the body below scrolls, so the task and its object
            stay named through every drill-in (review notes 3 and 4). */}
        <View style={[styles.header, { borderBottomColor: colors.border }]}>
          {folded && (
            <Pressable
              onPress={onClose}
              testID="companion-back"
              accessibilityRole="button"
              accessibilityLabel="Back"
              aria-label="Back"
              hitSlop={8}
              style={styles.backButton}>
              <ArrowLeft size={20} color={colors.text} />
            </Pressable>
          )}
          <View style={styles.titleBlock}>
            {overline ? (
              <Text style={[styles.overline, { color: colors.textSecondary }]} numberOfLines={1}>
                {overline}
              </Text>
            ) : null}
            <Text
              nativeID={headingId}
              style={[styles.title, { color: colors.text }]}
              numberOfLines={overline ? 2 : 1}>
              {title}
            </Text>
            {subtitle ? (
              <Text style={[styles.subtitle, { color: colors.textSecondary }]} numberOfLines={1}>
                {subtitle}
              </Text>
            ) : null}
          </View>
          {!folded && (
            <TouchableOpacity
              onPress={onClose}
              testID="companion-close"
              accessibilityRole="button"
              accessibilityLabel="Close"
              hitSlop={8}>
              <X size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
        {/* No scrollbar track, like the plan panel's own list, and no
            sideways scrolling: a full-height gray track showed down the
            companion's edge (Sept 28). */}
        <ScrollView
          style={styles.body}
          contentContainerStyle={styles.bodyContent}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}>
          {children}
        </ScrollView>
      </View>
    </DockedPanel>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    minHeight: PLAN_PANEL_HEADER_HEIGHT,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xxs,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xxs,
  },
  body: {
    flex: 1,
    overflowX: 'hidden',
  } as object,
  bodyContent: {
    flexGrow: 1,
  },
  backButton: {
    padding: SPACING.xs,
    marginLeft: -SPACING.xs,
  },
});
